/**
 * Dialog: a modal window (cockatrice-ui `src/ui/Dialog.jsx`, ported to hyperscript; PLAN.md §0). On phones it becomes
 * a full-screen sheet with a sticky header and footer.
 * Put the primary button LAST in `footer`: it sits on the right on desktop and on top on phones.
 *
 * Modal plumbing shared by Dialog, ConfirmDialog, BottomSheet and Drawer.
 * - the rest of the page becomes inert while a modal is open
 * - focus moves to the first text field, or else to the panel itself (never to an option)
 * - Tab stays inside; Escape closes the top-most modal only; focus returns to the opener
 * - page scroll is locked (counted, so stacked modals work)
 */
import './css/dialog.css';
import { createUniqueId, mergeProps, onCleanup, onMount, splitProps } from 'solid-js';
import Icon from './Icon.ts';
import { uiText } from './text.ts';
import { component, dynamic, el, portal, when, whenSome } from './solid.ts';
import type { Attributes, Child } from './solid.ts';

interface ModalEntry {
  readonly panel: HTMLElement;
}

const stack: ModalEntry[] = [];
let locks = 0;

const TEXT_FIELDS = [
  'input:not([type])',
  'input[type="text"]',
  'input[type="email"]',
  'input[type="password"]',
  'input[type="search"]',
  'input[type="url"]',
  'input[type="tel"]',
  'input[type="number"]',
  'textarea',
  '[contenteditable="true"]',
]
  .map((s) => `${s}:not([disabled]):not([readonly])`)
  .join(',');

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'iframe',
  'summary',
  '[tabindex]:not([tabindex="-1"])',
  '[contenteditable="true"]',
].join(',');

const shown = (node: Element): boolean => node.getClientRects().length > 0;

/** Where focus lands on open: a selector inside the panel, an element, or a function giving one. */
export type InitialFocus = string | HTMLElement | (() => HTMLElement | null);

export interface ModalOptions {
  /** Called when Escape is pressed and this is the top-most modal. */
  readonly onEscape?: (() => void) | undefined;
  /** Where focus lands. Default: first text field, else the panel. */
  readonly initialFocus?: InitialFocus | undefined;
}

/** Every element Tab can reach inside root, in order. */
export function focusables(root: HTMLElement): HTMLElement[] {
  return [...root.querySelectorAll<HTMLElement>(FOCUSABLE)].filter((node) => shown(node) && node.closest('[inert]') === null);
}

/** Make an element modal (inert page, focus trap, Escape, scroll lock, focus return). Returns an undo function. */
export function activateModal(panel: HTMLElement, opts: ModalOptions = {}): () => void {
  const opener = document.activeElement;
  const entry: ModalEntry = { panel };
  stack.push(entry);

  const host = [...document.body.children].find((c) => c.contains(panel));
  const inerted: HTMLElement[] = [];
  for (const child of document.body.children) {
    if (!(child instanceof HTMLElement)) continue;
    if (child === host || child.inert || child.tagName === 'SCRIPT' || child.tagName === 'STYLE') continue;
    if (child.matches('[data-ui-keep-live]') || child.querySelector(':scope > [data-ui-keep-live]') !== null) continue; // toasts stay announced
    child.inert = true;
    inerted.push(child);
  }
  if (locks++ === 0) document.documentElement.classList.add('scroll-locked');

  const focusFirst = (): void => {
    const f = opts.initialFocus;
    let target: HTMLElement | null | undefined =
      typeof f === 'function' ? f() : typeof f === 'string' ? panel.querySelector<HTMLElement>(f) : f instanceof HTMLElement ? f : null;
    target ??= [...panel.querySelectorAll<HTMLElement>(TEXT_FIELDS)].find(shown);
    (target ?? panel).focus({ preventScroll: true });
  };
  if (panel.isConnected) focusFirst();
  else requestAnimationFrame(focusFirst);

  const onKey = (e: KeyboardEvent): void => {
    if (stack.at(-1) !== entry || e.defaultPrevented) return;
    if (e.key === 'Escape') {
      if (opts.onEscape) {
        e.preventDefault();
        opts.onEscape();
      }
      return;
    }
    if (e.key !== 'Tab') return;
    const list = focusables(panel);
    const first = list[0];
    const last = list.at(-1);
    if (first === undefined || last === undefined) {
      e.preventDefault();
      panel.focus();
      return;
    }
    const active = document.activeElement;
    if (e.shiftKey && (active === first || active === panel || !panel.contains(active))) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && (active === last || !panel.contains(active))) {
      e.preventDefault();
      first.focus();
    }
  };
  document.addEventListener('keydown', onKey);

  return () => {
    document.removeEventListener('keydown', onKey);
    const i = stack.indexOf(entry);
    if (i > -1) stack.splice(i, 1);
    for (const node of inerted) node.inert = false;
    if (--locks === 0) document.documentElement.classList.remove('scroll-locked');
    if (opener?.isConnected === true && (opener instanceof HTMLElement || opener instanceof SVGElement)) opener.focus({ preventScroll: true });
  };
}

/** Pointer handlers for a scrim that call shouldClose on a click that starts and ends on it (not a drag out of a text field). */
export function scrimHandlers(shouldClose: () => void): { onPointerDown: (e: PointerEvent) => void; onClick: (e: MouseEvent) => void } {
  let down = false;
  return {
    onPointerDown: (e) => {
      down = e.target === e.currentTarget;
    },
    onClick: (e) => {
      if (down && e.target === e.currentTarget) shouldClose();
      down = false;
    },
  };
}

export interface DialogProps {
  /** Show the dialog. */
  readonly open?: boolean | undefined;
  /** Called by the close button, Escape and the scrim. Without it there is no close button. */
  readonly onClose?: (() => void) | undefined;
  /** Visible heading (serif). Becomes the accessible name. */
  readonly title?: Child;
  /** Accessible name when there is no visible title. */
  readonly label?: string | undefined;
  /** Small caps line above the title, e.g. "Step 2 of 3". */
  readonly eyebrow?: Child;
  /** One line under the title, linked with aria-describedby. */
  readonly description?: Child;
  /** The body. Scrolls when tall. */
  readonly children?: Child;
  /** Footer buttons. Put the primary LAST: right on desktop, on top on phones. */
  readonly footer?: Child;
  /** Quiet text on the left of the footer. */
  readonly footerStart?: Child;
  /** 420, 560 or 820 px wide. Default "md". */
  readonly size?: 'sm' | 'md' | 'lg' | undefined;
  /** Makes the panel a <form>; Enter submits. preventDefault is already called. */
  readonly onSubmit?: ((e: SubmitEvent) => void) | undefined;
  /** Close when the scrim is clicked. Default true, or false when onSubmit is set. */
  readonly closeOnScrim?: boolean | undefined;
  /** False hides the close button and ignores Escape and the scrim (e.g. while saving). Default true. */
  readonly dismissible?: boolean | undefined;
  /** Where focus lands on open. Default: first text field, else the panel itself. */
  readonly initialFocus?: InitialFocus | undefined;
  /** Accessible name of the close button. Default "Close". */
  readonly closeLabel?: string | undefined;
  /** Render in place with no scrim, portal or focus trap (galleries, previews). */
  readonly inline?: boolean | undefined;
  /** Default "dialog". ConfirmDialog uses "alertdialog". */
  readonly role?: 'dialog' | 'alertdialog' | undefined;
  /** Paints the panel for a destructive context (used by ConfirmDialog). */
  readonly tone?: 'default' | 'danger' | undefined;
  /** Extra class on the panel. */
  readonly class?: string | undefined;
}

type PanelProps = DialogProps & { readonly modal: boolean };

/** The head of a dialog: its name (and a line above and under it) and Close. */
export interface DialogHeadProps {
  /** The dialog's name. */
  readonly title?: Child;
  /** The name's id (otherwise generated): what the dialog is labelled by. */
  readonly titleId?: string | undefined;
  /** The name can take the focus from script (tabindex="-1"): a long dialog opens at its start. */
  readonly titleFocusable?: boolean | undefined;
  /** The name takes the dialog's first focus (autofocus), before its first control. */
  readonly titleAutofocus?: boolean | undefined;
  /** A small line above the name (a step: "Step 2 of 3"). */
  readonly eyebrow?: Child;
  /** One line under the name. */
  readonly description?: Child;
  /** The description's id (otherwise generated). */
  readonly descriptionId?: string | undefined;
  /** Shows Close, which calls this. */
  readonly onClose?: (() => void) | undefined;
  /** Close's name. Default "Close". */
  readonly closeLabel?: string | undefined;
  /** A class Close carries besides its own (what a page finds it by). */
  readonly closeClass?: string | undefined;
}

/**
 * The head (PLAN.md §3, step 3: the parts are exported so a page with its own native <dialog> can build the same
 * panel). A plain box, not <header>: inside a dialog a header is exposed as the page's banner landmark.
 */
export function DialogHead(props: DialogHeadProps): Child {
  const id = createUniqueId();
  const filled = (value: unknown): boolean => Boolean(value);
  return el(
    'div',
    { class: 'ui-dialog__head' },
    el(
      'div',
      { class: 'ui-dialog__titles' },
      when(
        () => filled(props.eyebrow),
        () => el('p', { class: 'ui-dialog__eyebrow' }, () => props.eyebrow),
      ),
      when(
        () => filled(props.title),
        () =>
          el(
            'h2',
            {
              id: () => props.titleId ?? `ui-dialog-${id}-t`,
              class: 'ui-dialog__title',
              tabIndex: () => (props.titleFocusable === true ? -1 : undefined),
              // Set once, as the head is made: a native <dialog>'s showModal() reads it at once to choose its first focus.
              ...(props.titleAutofocus === true ? { autofocus: true } : {}),
            },
            () => props.title,
          ),
      ),
      when(
        () => filled(props.description),
        () => el('p', { id: () => props.descriptionId ?? `ui-dialog-${id}-d`, class: 'ui-dialog__desc' }, () => props.description),
      ),
    ),
    whenSome(
      () => props.onClose,
      (close) =>
        el(
          'button',
          {
            type: 'button',
            class: () => (props.closeClass === undefined ? 'ui-dialog__close' : `ui-dialog__close ${props.closeClass}`),
            'aria-label': () => props.closeLabel ?? uiText()('ui.close'),
            onClick: () => {
              close()();
            },
          },
          component(Icon, { name: 'close' }),
        ),
    ),
  );
}

/** The body, which scrolls between the head and the foot. Extra attributes go on its box. */
export function DialogBody(props: Attributes & { readonly class?: string | undefined; readonly children?: Child }): Child {
  const [local, rest] = splitProps(props, ['class', 'children']);
  return el('div', mergeProps(rest, { class: () => (local.class === undefined ? 'ui-dialog__body' : `ui-dialog__body ${local.class}`) }), () => local.children);
}

/** The foot: a note (DialogNote) at its start and the actions (DialogActions) at its end. Not <footer>, as the head. */
export function DialogFoot(props: Attributes & { readonly class?: string | undefined; readonly children?: Child }): Child {
  const [local, rest] = splitProps(props, ['class', 'children']);
  return el('div', mergeProps(rest, { class: () => (local.class === undefined ? 'ui-dialog__foot' : `ui-dialog__foot ${local.class}`) }), () => local.children);
}

/** The foot's actions, the primary last: on the right on a desktop, on top on a phone. */
export function DialogActions(props: { readonly children?: Child }): Child {
  return el('div', { class: 'ui-dialog__actions' }, () => props.children);
}

/** The foot's note, at its start: a small line of help. */
export function DialogNote(props: { readonly children?: Child }): Child {
  return el('div', { class: 'ui-dialog__foot-start' }, () => props.children);
}

/**
 * The panel alone, for a page whose own native <dialog> (opened with showModal()) already traps the focus, makes the page
 * inert, closes on Escape and gives the focus back: put it in `<dialog class="ui-dialog-host">` with the parts inside.
 * It is not a dialog itself (no role): the <dialog> is.
 */
export function DialogPanel(props: { readonly size?: 'sm' | 'md' | 'lg' | undefined; readonly tone?: 'default' | 'danger' | undefined; readonly class?: string | undefined; readonly children?: Child }): Child {
  return el(
    'div',
    {
      class: () =>
        `ui-dialog__panel ui-dialog__panel--${props.size ?? 'md'}${props.tone === 'danger' ? ' ui-dialog__panel--danger' : ''}${props.class !== undefined && props.class !== '' ? ` ${props.class}` : ''}`,
    },
    () => props.children,
  );
}

function Panel(props: PanelProps): Child {
  let panel: HTMLElement | undefined;
  const id = createUniqueId();
  const titleId = `ui-dialog-${id}-t`;
  const descId = `ui-dialog-${id}-d`;
  const dismissible = (): boolean => props.dismissible !== false;

  onMount(() => {
    if (!props.modal || panel === undefined) return;
    const off = activateModal(panel, {
      initialFocus: props.initialFocus,
      onEscape: () => {
        if (dismissible()) props.onClose?.();
      },
    });
    onCleanup(off);
  });

  const isTrue = (value: unknown): boolean => Boolean(value);

  return dynamic(
    () => (props.onSubmit ? 'form' : 'div'),
    {
      ref: (node: HTMLElement) => {
        panel = node;
      },
      class: () =>
        `ui-dialog__panel ui-dialog__panel--${props.size ?? 'md'}${props.tone === 'danger' ? ' ui-dialog__panel--danger' : ''}${props.class !== undefined && props.class !== '' ? ` ${props.class}` : ''}`,
      role: () => props.role ?? 'dialog',
      'aria-modal': () => (props.modal ? 'true' : undefined),
      'aria-labelledby': () => (isTrue(props.title) ? titleId : undefined),
      'aria-label': () => (isTrue(props.title) ? undefined : props.label),
      'aria-describedby': () => (isTrue(props.description) ? descId : undefined),
      tabindex: '-1',
      noValidate: () => (props.onSubmit ? true : undefined),
      // The JSX sets this handler only on the form; a div never submits, and one bubbling up from a form inside is ignored.
      onSubmit: (e: SubmitEvent) => {
        const submit = props.onSubmit;
        if (!submit) return;
        e.preventDefault();
        submit(e);
      },
    },
    component(DialogHead, {
      titleId,
      descriptionId: descId,
      get title() {
        return props.title;
      },
      get eyebrow() {
        return props.eyebrow;
      },
      get description() {
        return props.description;
      },
      get closeLabel() {
        return props.closeLabel;
      },
      get onClose() {
        const close = props.onClose;
        return close !== undefined && dismissible() ? close : undefined;
      },
    }),
    component(DialogBody, {
      get children() {
        return props.children;
      },
    }),
    when(
      () => isTrue(props.footer) || isTrue(props.footerStart),
      () =>
        component(DialogFoot, {
          get children() {
            return [
              when(
                () => isTrue(props.footerStart),
                () =>
                  component(DialogNote, {
                    get children() {
                      return props.footerStart;
                    },
                  }),
              ),
              component(DialogActions, {
                get children() {
                  return props.footer;
                },
              }),
            ];
          },
        }),
    ),
  );
}

/**
 * A modal window. On phones it becomes a full-screen sheet with a sticky header and footer.
 * Put the primary button LAST in `footer`: it sits on the right on desktop and on top on phones.
 */
export default function Dialog(props: DialogProps): Child {
  const closeOnScrim = (): boolean => props.closeOnScrim ?? !props.onSubmit;
  const scrim = scrimHandlers(() => {
    if (closeOnScrim() && props.dismissible !== false) props.onClose?.();
  });
  return when(
    () => props.inline === true,
    () => el('div', { class: 'ui-dialog ui-dialog--inline' }, component(Panel, mergeProps(props, { modal: false }))),
    () =>
      when(
        () => props.open === true,
        () => portal(() => el('div', { class: 'ui-dialog', onPointerDown: scrim.onPointerDown, onClick: scrim.onClick }, component(Panel, mergeProps(props, { modal: true })))),
      ),
  );
}
